import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';

interface NewsContentSection {
  content: string;
  image?: string;
}

interface NewsItem {
  id: number;
  title: string;
  summary: string;
  content: string;
  contentSections?: NewsContentSection[]; // 分段内容，每段可配图片
  date: string;
  category: string;
  image?: string;
  images?: string[]; // 支持多张图片
  featured: boolean;
}

const LEGACY_CATEGORY_MAP: Record<string, string> = {
  '公司动态': 'company',
  '行业资讯': 'industry',
  '产品发布': 'product',
  '合作签约': 'cooperation',
  '获奖荣誉': 'awards',
};

const DEFAULT_NEWS_COVER = '/images/main/product.webp';

const News: React.FC = () => {
  const { t, i18n } = useTranslation();
  const bannerImagePath = '/images/news/banner.webp';

  const getNewsCoverImage = (news: NewsItem): string => {
    return news.image || DEFAULT_NEWS_COVER;
  };
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const normalizeCategory = (category: string): string => {
    return LEGACY_CATEGORY_MAP[category] || category;
  };

  const getCategoryLabel = (category: string): string => {
    const key = normalizeCategory(category);
    const i18nKey = `news.categories.${key}`;
    const translated = t(i18nKey);
    return translated !== i18nKey ? translated : category;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const itemsPerPage = 6;

  // 新闻分类
  const categories = [
    { id: 'all', label: t('news.categories.all') },
    { id: 'company', label: t('news.categories.company') },
    { id: 'industry', label: t('news.categories.industry') },
    { id: 'product', label: t('news.categories.product') },
    { id: 'cooperation', label: t('news.categories.cooperation') },
    { id: 'awards', label: t('news.categories.awards') }
  ];

  // 加载新闻数据
  useEffect(() => {
    const loadNews = async () => {
      try {
        // 优先从 localStorage 读取语言设置（确保新标签页也能正确读取）
        const storedLang = localStorage.getItem('i18nextLng');
        const langFromStorage = storedLang ? storedLang.split('-')[0] : null;
        const langFromI18n = i18n.language?.split('-')[0] || 'zh';
        const currentLang = langFromStorage || langFromI18n;
        
        let newsResponse;
        if (currentLang === 'en') {
          // 尝试加载英文版本
          newsResponse = await fetch('/data/news-en.json');
          if (!newsResponse.ok) {
            // 如果英文版本不存在，回退到中文版本
            newsResponse = await fetch('/data/news-zh.json');
          }
        } else {
          // 加载中文版本
          newsResponse = await fetch('/data/news-zh.json');
        }
        
        const newsDataFile = await newsResponse.json();
        setNewsData(newsDataFile.news || []);
      } catch (error) {
        console.error('加载新闻数据失败:', error);
        setNewsData([]);
      }
    };
    
    // 等待 i18n 初始化完成后再加载数据
    if (i18n.isInitialized) {
      loadNews();
    } else {
      const initHandler = () => {
        loadNews();
        i18n.off('initialized', initHandler);
      };
      i18n.on('initialized', initHandler);
    }

    // 监听语言变化事件
    const handleLanguageChanged = () => {
      loadNews();
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  // 临时新闻数据（作为fallback，如果JSON文件不存在）
  const fallbackNewsData: NewsItem[] = [
    {
      id: 1,
      title: '香遇七夕 | 气息为约，予你不散的浪漫',
      summary: '在一年一度的七夕佳节，浪漫与期盼如约而至。当温柔的夜色与缱绻的心意交织，专属于七夕的悸动，便在空气中缓缓弥散。',
      content: '在一年一度的七夕佳节，浪漫与期盼如约而至。\n\n当温柔的夜色与缱绻的心意交织，专属于七夕的悸动，便在空气中缓缓弥散。\n\n柯熙美诺香水代加工厂，精心调配每一款香氛，只为用心守护每一段美好的情感。\n\n当气味与气味相遇，即是灵魂与灵魂的契合，一缕专属的香气，便成了爱的信物。\n\n无需华丽的言语，只需一抹柔和的香气，柯熙美诺香水代加工厂，皆愿将每一份爱意与祝福封存于瓶中，让香氛化作情感的桥梁，成为七夕节里你最独特的表达。',
      contentSections: [
        {
          content: '在一年一度的七夕佳节，浪漫与期盼如约而至。\n\n当温柔的夜色与缱绻的心意交织，专属于七夕的悸动，便在空气中缓缓弥散。\n\n柯熙美诺香水代加工厂，精心调配每一款香氛，只为用心守护每一段美好的情感。\n\n当气味与气味相遇，即是灵魂与灵魂的契合，一缕专属的香气，便成了爱的信物。\n\n无需华丽的言语，只需一抹柔和的香气，柯熙美诺香水代加工厂，皆愿将每一份爱意与祝福封存于瓶中，让香氛化作情感的桥梁，成为七夕节里你最独特的表达。',
          image: '/images/news/20250829-1.webp'
        }
      ],
      date: '2025-08-29',
      category: 'product',
      image: '/images/news/20250829-1.webp',
      featured: true
    },
    {
      id: 2,
      title: '2025年9月2日-4日 | 香遇拉美，共创芳华',
      summary: '柯熙美诺香水代加工工厂将在2025年9月2日至9月4日，亮相墨西哥展会，作为高端定制香水代工领域的专业品牌，我们将携最新配方、爆款香型、全方位ODM/OEM解决方案。',
      content: '柯熙美诺香水代加工工厂将在2025年9月2日至9月4日，亮相墨西哥展会，作为高端定制香水代工领域的专业品牌，我们将携最新配方、爆款香型、全方位ODM/OEM解决方案，与全球合作伙伴共襄盛会。\n\n无论您想打造专属品牌，还是探索拉美市场商机，欢迎莅临柯熙美诺展台，体验一站式香水定制支持，与我们一起开启香氛市场新「香」遇！\n\n【高端精品香水香精】这次我们精选了数十种高端香水香精亮相展会，以香气多元化，地区特色鲜明，市场适配度高等，多项优势受到了许多客户的青睐。\n\n【特色香亮定制方案】本次展会我们不仅带去多样的香气，还带去了香水全流程定制服务，我们根据客户需求为客户提供更细致全面的OEM服务，同时也带去了一些定制成品受到客户的关注。\n\n【未来展望】本次展会不仅是产品的展示，更是深耕日化香亮领域的重要里程碑。\n\n我们将持续探索多元香亮科技，优香客制香亮方案，为全球客户提供更卓越的香氛体验。',
      contentSections: [
        {
          content: '柯熙美诺香水代加工工厂将在2025年9月2日至9月4日，亮相墨西哥展会，作为高端定制香水代工领域的专业品牌，我们将携最新配方、爆款香型、全方位ODM/OEM解决方案，与全球合作伙伴共襄盛会。\n\n无论您想打造专属品牌，还是探索拉美市场商机，欢迎莅临柯熙美诺展台，体验一站式香水定制支持，与我们一起开启香氛市场新「香」遇！',
          image: '/images/news/20250905-1.webp'
        },
        {
          content: '【高端精品香水香精】\n\n这次我们精选了数十种高端香水香精亮相展会，以香气多元化，地区特色鲜明，市场适配度高等，多项优势受到了许多客户的青睐。',
          image: '/images/news/20250905-2.webp'
        },
        {
          content: '【特色香亮定制方案】\n\n本次展会我们不仅带去多样的香气，还带去了香水全流程定制服务，我们根据客户需求为客户提供更细致全面的OEM服务，同时也带去了一些定制成品受到客户的关注。',
          image: '/images/news/20250905-3.webp'
        },
        {
          content: '【未来展望】\n\n本次展会不仅是产品的展示，更是深耕日化香亮领域的重要里程碑。\n\n我们将持续探索多元香亮科技，优香客制香亮方案，为全球客户提供更卓越的香氛体验。',
          image: '/images/news/20250905-4.webp'
        }
      ],
      date: '2025-09-05',
      category: 'company',
      image: '/images/news/20250905-1.webp',
      images: [
        '/images/news/20250905-1.webp',
        '/images/news/20250905-2.webp',
        '/images/news/20250905-3.webp',
        '/images/news/20250905-4.webp'
      ],
      featured: true
    },
    {
      id: 3,
      title: '3.8妇女节特辑 | 爱自己是浪漫的开始',
      summary: '妇女节，是属于每一位女性的节日。这一天，不仅是赞美与致敬，更提醒我们：自爱，是所有浪漫的起点。与其等待被爱，不如主动拥抱美好生活。',
      content: '妇女节，是属于每一位女性的节日。\n\n这一天，不仅是赞美与致敬，更提醒我们：自爱，是所有浪漫的起点。\n\n与其等待被爱，不如主动拥抱美好生活，用一瓶专属的香氛，为自己点亮温柔与力量——无论你身处何地，身兼多少角色，都值得拥有散发自信与魅力的独特气息。\n\n今天，我们特意甄选5款能为你增添仪式感与幸福感的香水，无论是送给自己，还是赠予身边的女性，都能传递温暖与自爱。\n\n【自由万岁】以甜美的葡萄香气带着清新花果香直白的闯入这个世界。\n\n青春的气息散在阳光里，就像好奇的精灵误闯一片茉莉和橙花的花海之中，带着懵懂而又轻快的舞步穿梭在一朵又一朵花尖，欢快又自由，青春又无畏。\n\n【我即是我】大面积的白花铺底，如同穿着一身利落西装，精致妆容自信而又沉稳的女强人，自然而然散发的强大气场。\n\n尾随而来的玫瑰又给气场中带了一丝柔情，海洋、青花、绿叶想从每个细微之处慢慢散出馥郁而又多样的气息，形成独特的香气。\n\n是了，我本来就是复杂的，我是成为任何我想成为的人，可以强硬也可以温柔，属于骄阳也勇于对抗低谷的勇士。\n\n【慵懒冬夜】裹着羊绒毛毯，看火焰跳动，焦糖与橙花点燃暖意，开司米木与愈创木醇厚香甜。\n\n它不张扬，却温暖随性，适合窝在沙发里自由放空，做最真实的自己。\n\n【打破常规】粉红胡椒与温暖木质调交织，温柔却有态度，起初如第一缕光穿透迷雾，而后又似图书馆里的静谧，后调沉稳有力。\n\n它不迎合、不讨好，突破常规、打破刻板、做自己的你，温柔与力量并存，清冷与温暖平衡，书写独特气质。\n\n在这个专属于我们的节日，不妨选一瓶心动的香氛，把它作为生活中独特的自爱契机。\n\n晨起时轻轻一喷，为忙碌的自己加冕；夜晚独处时细嗅余香，犒赏每一份努力和温柔。\n\n你会发现，爱自己，是人生中最温柔也最有力量的浪漫。\n\n愿每一位女生都能忠于自我、明媚如初。\n\n让香气成为自信起舞的裙摆，让爱自己成为这一生最美的起点。',
      contentSections: [
        {
          content: '妇女节，是属于每一位女性的节日。这一天，不仅是赞美与致敬，更提醒我们：自爱，是所有浪漫的起点。与其等待被爱，不如主动拥抱美好生活，用一瓶专属的香氛，为自己点亮温柔与力量——无论你身处何地，身兼多少角色，都值得拥有散发自信与魅力的独特气息。\n\n今天，我们特意甄选5款能为你增添仪式感与幸福感的香水，无论是送给自己，还是赠予身边的女性，都能传递温暖与自爱。',
          image: '/images/news/20250307-1.webp'
        },
        {
          content: '【自由万岁】\n\n以甜美的葡萄香气带着清新花果香直白的闯入这个世界。\n\n青春的气息散在阳光里，就像好奇的精灵误闯一片茉莉和橙花的花海之中，带着懵懂而又轻快的舞步穿梭在一朵又一朵花尖，欢快又自由，青春又无畏。',
          image: '/images/news/20250307-2.webp'
        },
        {
          content: '【我即是我】\n\n大面积的白花铺底，如同穿着一身利落西装，精致妆容自信而又沉稳的女强人，自然而然散发的强大气场。\n\n尾随而来的玫瑰又给气场中带了一丝柔情，海洋、青花、绿叶想从每个细微之处慢慢散出馥郁而又多样的气息，形成独特的香气。\n\n是了，我本来就是复杂的，我是成为任何我想成为的人，可以强硬也可以温柔，属于骄阳也勇于对抗低谷的勇士。',
          image: '/images/news/20250307-3.webp'
        },
        {
          content: '【慵懒冬夜】\n\n裹着羊绒毛毯，看火焰跳动，焦糖与橙花点燃暖意，开司米木与愈创木醇厚香甜。\n\n它不张扬，却温暖随性，适合窝在沙发里自由放空，做最真实的自己。',
          image: '/images/news/20250307-4.webp'
        },
        {
          content: '【打破常规】\n\n粉红胡椒与温暖木质调交织，温柔却有态度，起初如第一缕光穿透迷雾，而后又似图书馆里的静谧，后调沉稳有力。\n\n它不迎合、不讨好，突破常规、打破刻板、做自己的你，温柔与力量并存，清冷与温暖平衡，书写独特气质。\n\n在这个专属于我们的节日，不妨选一瓶心动的香氛，把它作为生活中独特的自爱契机。\n\n晨起时轻轻一喷，为忙碌的自己加冕；夜晚独处时细嗅余香，犒赏每一份努力和温柔。\n\n你会发现，爱自己，是人生中最温柔也最有力量的浪漫。愿每一位女生都能忠于自我、明媚如初。让香气成为自信起舞的裙摆，让爱自己成为这一生最美的起点。',
          image: '/images/news/20250307-5.webp'
        }
      ],
      date: '2025-03-07',
      category: 'product',
      image: '/images/news/20250307-1.webp',
      images: [
        '/images/news/20250307-1.webp',
        '/images/news/20250307-2.webp',
        '/images/news/20250307-3.webp',
        '/images/news/20250307-4.webp',
        '/images/news/20250307-5.webp'
      ],
      featured: true
    },
    {
      id: 4,
      title: '你知道吗？淡香水、香精、古龙水的区别',
      summary: '在选购香水时，很多人常常会被"淡香水"、"香精"、"古龙水"等词汇搞糊涂：它们到底有什么不同？每种适合什么场合？为什么价格和留香时间有区别？',
      content: '在选购香水时，很多人常常会被"淡香水"、"香精"、"古龙水"等词汇搞糊涂：它们到底有什么不同？每种适合什么场合？为什么价格和留香时间有区别？\n\n如果你也有这样的疑问，这篇文章将为你揭开答案！\n\n一、什么是香水的"浓度"？\n香水的种类有很多，但最核心的区别，来源于香料在酒精溶液中的浓度，也叫做"香精油含量"。\n\n浓度直接决定了香水的香气强度、留香时间和使用感受。\n\n从高到低主要分为：\n1. 香精(Parfum/Extrait de Parfum)\n2. 淡香精(Eau de Parfum，简称EDP)\n3. 淡香水(Eau de Toilette，简称EDT)\n4. 古龙水(Eau de Cologne，简称EDC)\n\n二、香精 Parfum/Extrait de Parfum\n浓度：香精油含量最高，一般15%~40%。\n\n留香时间：8~12小时，部分可达24小时以上。\n\n香气特点：香气丰富、层次感强，散发力持久。\n\n由于高浓度酒精较低，对皮肤刺激性小。\n\n用量非常省，适合重要场合或想要展现仪式感时使用。\n\n三、淡香精 Eau de Parfum(EDP)\n浓度：香精油含量较高，约12%~18%。\n\n留香时间：6~8小时，工作一天基本能维持。\n\n香气特点：香气浓郁但不会过于浓烈，适合日常及正式场合。\n\n大多品牌经典女香以EDP为主。\n\n四、淡香水 Eau de Toilette(EDT)\n浓度：香精油含量较低，约5%~12%。\n\n留香时间：3~5小时，香气清新淡雅，适合随时补喷。\n\n香气特点：层次简洁、清爽透气，适合夏天或希望营造干净氛围的人。\n\n很多男士香水产品以EDT为主，女香也常有"同款淡香水"与"淡香精"区别。\n\n五、古龙水 Eau de Cologne(EDC)\n浓度：香精油含量较低，仅2%~5%。\n\n留香时间：1~3小时，留香最短，需频繁补喷。\n\n香气特点：多为柑橘、青草、薰衣草等清新调，给人清爽、提神的印象。\n\n早期多为男士个护产品，现也有很多中性香的古龙水。\n\n每一种香水都有它独特的魅力与适用场景。\n\n了解香精、淡香精、淡香水和古龙水的区别，能帮助你正确选择最适合自己的香氛，享受香气带来的美好生活。\n\n想了解更多香水科普和选购建议，记得关注我们的香水资讯专栏，或咨询专业香水顾问哦！',
      contentSections: [
        {
          content: '在选购香水时，很多人常常会被"淡香水"、"香精"、"古龙水"等词汇搞糊涂：它们到底有什么不同？每种适合什么场合？为什么价格和留香时间有区别？\n\n如果你也有这样的疑问，这篇文章将为你揭开答案！\n\n一、什么是香水的"浓度"？\n香水的种类有很多，但最核心的区别，来源于香料在酒精溶液中的浓度，也叫做"香精油含量"。\n\n浓度直接决定了香水的香气强度、留香时间和使用感受。\n\n从高到低主要分为：\n1. 香精(Parfum/Extrait de Parfum)\n2. 淡香精(Eau de Parfum，简称EDP)\n3. 淡香水(Eau de Toilette，简称EDT)\n4. 古龙水(Eau de Cologne，简称EDC)',
          image: '/images/news/20250102-1.webp'
        },
        {
          content: '二、香精 Parfum/Extrait de Parfum\n浓度：香精油含量最高，一般15%~40%。\n\n留香时间：8~12小时，部分可达24小时以上。\n\n香气特点：香气丰富、层次感强，散发力持久。\n\n由于高浓度酒精较低，对皮肤刺激性小。\n\n用量非常省，适合重要场合或想要展现仪式感时使用。',
          image: '/images/news/20250102-2.webp'
        },
        {
          content: '三、淡香精 Eau de Parfum(EDP)\n浓度：香精油含量较高，约12%~18%。\n\n留香时间：6~8小时，工作一天基本能维持。\n\n香气特点：香气浓郁但不会过于浓烈，适合日常及正式场合。\n\n大多品牌经典女香以EDP为主。',
          image: '/images/news/20250102-3.webp'
        },
        {
          content: '四、淡香水 Eau de Toilette(EDT)\n浓度：香精油含量较低，约5%~12%。\n\n留香时间：3~5小时，香气清新淡雅，适合随时补喷。\n\n香气特点：层次简洁、清爽透气，适合夏天或希望营造干净氛围的人。\n\n很多男士香水产品以EDT为主，女香也常有"同款淡香水"与"淡香精"区别。',
          image: '/images/news/20250102-4.webp'
        },
        {
          content: '五、古龙水 Eau de Cologne(EDC)\n浓度：香精油含量较低，仅2%~5%。\n\n留香时间：1~3小时，留香最短，需频繁补喷。\n\n香气特点：多为柑橘、青草、薰衣草等清新调，给人清爽、提神的印象。\n\n早期多为男士个护产品，现也有很多中性香的古龙水。\n\n每一种香水都有它独特的魅力与适用场景。\n\n了解香精、淡香精、淡香水和古龙水的区别，能帮助你正确选择最适合自己的香氛，享受香气带来的美好生活。\n\n想了解更多香水科普和选购建议，记得关注我们的香水资讯专栏，或咨询专业香水顾问哦！',
          image: '/images/news/20250102-5.webp'
        }
      ],
      date: '2025-01-02',
      category: 'industry',
      image: '/images/news/20250102-1.webp',
      images: [
        '/images/news/20250102-1.webp',
        '/images/news/20250102-2.webp',
        '/images/news/20250102-3.webp',
        '/images/news/20250102-4.webp',
        '/images/news/20250102-5.webp'
      ],
      featured: false
    },
    {
      id: 5,
      title: '2025展会回顾 | 柯熙美诺与您香约同行',
      summary: '新一年、新气象。柯熙美诺香水代加工厂将亮相2025上半年多场行业展会，携创新配方与匠心工艺，开启美好"香"遇之旅！',
      content: '新一年、新气象。\n\n柯熙美诺香水代加工厂将亮相2025上半年多场行业展会，携创新配方与匠心工艺，开启美好"香"遇之旅！\n\n无论您是品牌主理人、零售商还是香氛爱好者，欢迎莅临展会现场，近距离感受柯熙美诺的专业实力与独特魅力。探讨代加工合作、体验新品香气、获取定制方案——我们期待与您面对面交流，共同开启香氛产业新篇章！\n\n更多展会信息，敬请关注后续资讯。柯熙美诺，您的专属香水代加工专家，2025下半年展会等您来"香"会。',
      contentSections: [
        {
          content: '新一年、新气象。 \n\n柯熙美诺香水代加工厂将亮相2025上半年多场行业展会，携创新配方与匠心工艺，开启美好"香"遇之旅！',
          image: '/images/news/20250705-1.webp'
        },
        {
          content: '无论您是品牌主理人、零售商还是香氛爱好者，欢迎莅临展会现场，近距离感受柯熙美诺的专业实力与独特魅力。探讨代加工合作、体验新品香气、获取定制方案——我们期待与您面对面交流，共同开启香氛产业新篇章！\n\n更多展会信息，敬请关注后续资讯。柯熙美诺，您的专属香水代加工专家，2025下半年展会等您来"香"会。',
          image: '/images/news/20250705-2.webp'
        }
      ],
      date: '2025-07-05',
      category: 'company',
      image: '/images/news/20250705-1.webp',
      images: [
        '/images/news/20250705-1.webp',
        '/images/news/20250705-2.webp'
      ],
      featured: false
    },
    {
      id: 6,
      title: '什么是香水OEM？你想知道的都在这里！',
      summary: '在香水行业，经常能听到"OEM"这个词。那么OEM到底是什么？对于品牌商、创业者来说又有哪些好处？今天就为你揭开香水OEM的面纱！',
      content: '在香水行业，经常能听到"OEM"这个词。那么OEM到底是什么？对于品牌商、创业者来说又有哪些好处？今天就为你揭开香水OEM的面纱！\n\n一、什么是OEM？\nOEM，英文全称为"Original Equipment Manufacturer"，即"原始设备制造商"。简单来说，就是由专业的制造工厂，按照客户的要求，代为生产香水产品。客户可以专注于品牌、营销和销售，无需自己搭建工厂或研发配方。\n\n二、香水OEM有何优势？\n1. 省时省力\n无需自建工厂，只需提供要求，工厂全程协助生产，快速实现产品上线。\n2. 专业品质保障\n专业OEM工厂拥有成熟的配方研发团队、高标准生产线，香水品质有保障。\n3. 个性化定制\n可定制香型、包装、容量、品牌logo等，打造专属品牌形象，实现多样化产品线。\n4. 成本优化\n大规模生产压低单品成本，为品牌节省投资和运营费用。\n\n三、适合哪些客户？\n- 想建立自有香水品牌的创业者\n- 需要开发独特纪念礼品、合作定制产品的企业或组织\n- 希望拓展现有产品线的美妆、时尚品牌商\n\n四、OEM流程简述\n1. 需求沟通：品牌商与OEM厂家对香型、包装、Logo等进行方案确认。\n2. 打样测试：厂家按要求试制样品，客户确认香型与包装。\n3. 签署合同：双方确定数量、价格、交期等细节，签订合作协议。\n4. 正式生产：工厂批量生产，期间品质严格管控。\n5. 交付发货：产品完成后按时发货，品牌商即可销售自有香水产品。\n\n五、温馨提示\n选择专业靠谱的OEM厂家是保障产品质量的关键。建议多咨询、多对比，亲自考察生产资质与团队背景。\n\n结语\n想低门槛拥有自己的香水品牌？OEM是高效解决方案，让你的品牌梦想触手可及。如需OEM合作或定制香水相关建议，欢迎咨询我们的专业顾问！',
      contentSections: [
        {
          content: '在香水行业，经常能听到"OEM"这个词。那么OEM到底是什么？对于品牌商、创业者来说又有哪些好处？今天就为你揭开香水OEM的面纱！\n\n一、什么是OEM？\nOEM，英文全称为"Original Equipment Manufacturer"，即"原始设备制造商"。简单来说，就是由专业的制造工厂，按照客户的要求，代为生产香水产品。客户可以专注于品牌、营销和销售，无需自己搭建工厂或研发配方。',
          image: '/images/news/20241201-1.webp'
        },
        {
          content: '二、香水OEM有何优势？\n1. 省时省力\n无需自建工厂，只需提供要求，工厂全程协助生产，快速实现产品上线。\n2. 专业品质保障\n专业OEM工厂拥有成熟的配方研发团队、高标准生产线，香水品质有保障。\n3. 个性化定制\n可定制香型、包装、容量、品牌logo等，打造专属品牌形象，实现多样化产品线。\n4. 成本优化\n大规模生产压低单品成本，为品牌节省投资和运营费用。',
          image: '/images/news/20241201-2.webp'
        },
        {
          content: '三、适合哪些客户？\n1. 想建立自有香水品牌的创业者\n2. 需要开发独特纪念礼品、合作定制产品的企业或组织\n3. 希望拓展现有产品线的美妆、时尚品牌商',
          image: '/images/news/20241201-3.webp'
        },
        {
          content: '四、OEM流程简述\n1. 需求沟通：品牌商与OEM厂家对香型、包装、Logo等进行方案确认。\n2. 打样测试：厂家按要求试制样品，客户确认香型与包装。\n3. 签署合同：双方确定数量、价格、交期等细节，签订合作协议。\n4. 正式生产：工厂批量生产，期间品质严格管控。\n5. 交付发货：产品完成后按时发货，品牌商即可销售自有香水产品。',
          image: '/images/news/20241201-4.webp'
        },
        {
          content: '五、温馨提示\n选择专业靠谱的OEM厂家是保障产品质量的关键。建议多咨询、多对比，亲自考察生产资质与团队背景。',
          image: '/images/news/20241201-5.webp'
        },
        {
            content: '结语\n想低门槛拥有自己的香水品牌？\nOEM是高效解决方案，让你的品牌梦想触手可及。如需OEM合作或定制香水相关建议，欢迎咨询我们的专业顾问！'
        }
      ],
      date: '2024-12-01',
      category: 'industry',
      image: '/images/news/20241201-1.webp',
      images: [
        '/images/news/20241201-1.webp',
        '/images/news/20241201-2.webp',
        '/images/news/20241201-3.webp',
        '/images/news/20241201-4.webp',
        '/images/news/20241201-5.webp'
      ],
      featured: false
    }
  ];

  // 使用加载的数据，如果没有则使用fallback
  const displayNewsData = newsData.length > 0 ? newsData : fallbackNewsData;

  // 筛选新闻并按时间排序（最新的在前）
  const filteredNews = (selectedCategory === 'all' 
    ? displayNewsData 
    : displayNewsData.filter(news => normalizeCategory(news.category) === selectedCategory)
  ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // 分页
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNews = filteredNews.slice(startIndex, startIndex + itemsPerPage);


  // 处理阅读全文点击
  const handleReadMore = (news: NewsItem) => {
    setSelectedNews(news);
    setShowModal(true);
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };

  return (
    <main className="news-container">
      {/* 页面标题区域 */}
      <section className="news-hero-section">
        <div 
          className="news-hero-background"
          style={{
            backgroundImage: `url('${bannerImagePath}')`
          }}
        >
          <div className="news-hero-overlay">
            <div className="news-hero-container">
              <h1 className="news-hero-title">{t('news.title')}</h1>
              <p className="news-hero-subtitle">
                {t('news.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* 新闻列表区域 */}
      <section className="news-list-section">
        <div className="news-list-container">
          <div className="news-list-header">
            <h2 className="section-title">{t('news.latestNews')}</h2>
            
            {/* 分类筛选 */}
            <div className="news-category-filter">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-filter-btn ${
                    selectedCategory === category.id ? 'active' : ''
                  }`}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setCurrentPage(1);
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* 新闻列表 */}
          <div className="news-list-grid">
            {paginatedNews.map((news) => (
              <article key={news.id} className="news-list-item">
                <div className="news-item-image">
                  <img 
                    src={getNewsCoverImage(news)} 
                    alt={news.title}
                    className="news-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (!target.src.endsWith(DEFAULT_NEWS_COVER)) {
                        target.src = DEFAULT_NEWS_COVER;
                      }
                    }}
                  />
                  {news.images && news.images.length > 1 && (
                    <span className="news-image-count">{news.images.length}</span>
                  )}
                </div>
                <div className="news-item-content">
                  <div className="news-meta">
                    <span className="news-date">{news.date}</span>
                    <span className="news-category">{getCategoryLabel(news.category)}</span>
                  </div>
                  <h3 className="news-item-title">{news.title}</h3>
                  <p className="news-item-summary">{news.summary}</p>
                  <div className="news-item-actions">
                    <button 
                      className="read-more-btn"
                      onClick={() => handleReadMore(news)}
                    >
                      {t('news.readFull')}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="news-pagination">
              <button 
                className="pagination-btn prev-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                {t('news.prevPage')}
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`pagination-number ${
                      currentPage === page ? 'active' : ''
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button 
                className="pagination-btn next-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                {t('news.nextPage')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* 新闻详情模态框 */}
      {showModal && selectedNews && (
        <div className="news-modal-overlay" onClick={handleCloseModal}>
          <div className="news-modal" onClick={(e) => e.stopPropagation()}>
            <div className="news-modal-header">
              <h2 className="news-modal-title">{selectedNews.title}</h2>
              <button className="news-modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <div className="news-modal-body">
              <div className="news-modal-meta">
                <span className="news-modal-date">{selectedNews.date}</span>
                <span className="news-modal-category">{getCategoryLabel(selectedNews.category)}</span>
              </div>
              
              {/* 使用分段内容渲染 */}
              {selectedNews.contentSections && selectedNews.contentSections.length > 0 ? (
                <div className="news-modal-content-sections">
                  {selectedNews.contentSections.map((section, index) => (
                    <div key={index} className="news-modal-section">
                      {section.image && (
                        <div className="news-modal-section-image">
                          <img 
                            src={section.image} 
                            alt={`${selectedNews.title} - 图片 ${index + 1}`}
                            className="news-modal-image"
                          />
                        </div>
                      )}
                      <div className="news-modal-section-content">
                        {section.content.split('\n').map((paragraph, pIndex) => (
                          paragraph.trim() ? (
                            <p key={pIndex} className="news-modal-paragraph">
                              {paragraph}
                            </p>
                          ) : null
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* 回退到原始内容渲染 */
                <div className="news-modal-content">
                  {selectedNews.images && selectedNews.images.length > 0 && (
                    <div className="news-modal-images">
                      {selectedNews.images.map((image, index) => (
                        <img 
                          key={index}
                          src={image} 
                          alt={`${selectedNews.title} - 图片 ${index + 1}`}
                          className="news-modal-image"
                        />
                      ))}
                    </div>
                  )}
                  
                  <div className="news-modal-content-text">
                    {selectedNews.content.split('\n').map((paragraph, index) => (
                      <p key={index} className="news-modal-paragraph">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default News;
